import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

/**
 * Helper to fetch all rows from a Supabase table, bypassing the default 1000-row PostgREST limit.
 */
async function fetchAllRows(tableName, orderBy = 'created_at', ascending = false) {
  if (!isSupabaseConfigured()) return [];
  const BATCH_SIZE = 1000;
  let allData = [];
  let from = 0;
  let to = BATCH_SIZE - 1;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .order(orderBy, { ascending })
      .range(from, to);

    if (error) throw error;
    if (data && data.length > 0) {
      allData = allData.concat(data);
      if (data.length < BATCH_SIZE) {
        hasMore = false;
      } else {
        from += BATCH_SIZE;
        to += BATCH_SIZE;
      }
    } else {
      hasMore = false;
    }
  }
  return allData;
}

export const tenantsService = {
  async getAll() {
    return fetchAllRows('tenants', 'created_at', false);
  },

  async create(tenant) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('tenants')
      .insert([{
        name: tenant.name,
        email: tenant.email || '',
        aadhaar: tenant.aadhaar || '',
        mobile: tenant.mobile,
        room_no: tenant.room_no,
        room_type: tenant.room_type || 'Single',
        monthly_rent: Number(tenant.monthly_rent) || 0,
        security_deposit: Number(tenant.security_deposit) || 0,
        maintenance_fees: Number(tenant.maintenance_fees) || 0,
        date_of_joining: tenant.date_of_joining,
        floor: tenant.floor || 'Ground Floor',
        reversed_deposit: Boolean(tenant.reversed_deposit),
        vacated: Boolean(tenant.vacated),
        vacated_date: tenant.vacated_date ? tenant.vacated_date.split('T')[0] : null
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, tenant) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('tenants')
      .update({
        name: tenant.name,
        email: tenant.email,
        aadhaar: tenant.aadhaar,
        mobile: tenant.mobile,
        room_no: tenant.room_no,
        room_type: tenant.room_type,
        monthly_rent: Number(tenant.monthly_rent) || 0,
        security_deposit: Number(tenant.security_deposit) || 0,
        maintenance_fees: Number(tenant.maintenance_fees) || 0,
        date_of_joining: tenant.date_of_joining,
        floor: tenant.floor,
        reversed_deposit: Boolean(tenant.reversed_deposit),
        vacated: Boolean(tenant.vacated),
        vacated_date: tenant.vacated_date ? tenant.vacated_date.split('T')[0] : null
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('tenants')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const expensesService = {
  async getAll() {
    return fetchAllRows('expenses', 'expense_date', false);
  },

  async getCategories() {
    if (!isSupabaseConfigured()) return {};
    const { data, error } = await supabase
      .from('expense_categories')
      .select('*');
    if (error) throw error;
    
    // Map list of { category, subcategories } to { [category]: subcategories }
    const catMap = {};
    (data || []).forEach(row => {
      catMap[row.category] = Array.isArray(row.subcategories) ? row.subcategories : [];
    });
    return catMap;
  },

  async saveCategory(category, subcategories) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('expense_categories')
      .upsert({ category, subcategories })
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async deleteCategory(category) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('expense_categories')
      .delete()
      .eq('category', category);
    if (error) throw error;
    return true;
  },

  async create(expense) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('expenses')
      .insert([{
        description: expense.description,
        amount: Number(expense.amount),
        category: expense.category,
        subcategory: expense.subcategory || null,
        quantity: Number(expense.quantity) || 1,
        metric: expense.metric || 'units',
        expense_date: expense.expense_date
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, expense) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('expenses')
      .update({
        description: expense.description,
        amount: Number(expense.amount),
        category: expense.category,
        subcategory: expense.subcategory || null,
        quantity: Number(expense.quantity) || 1,
        metric: expense.metric || 'units',
        expense_date: expense.expense_date
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const invoicesService = {
  async getAll() {
    return fetchAllRows('invoices', 'payment_date', false);
  },

  async create(invoice) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('invoices')
      .insert([{
        receipt_no: invoice.receipt_no,
        name: invoice.name,
        email: invoice.email || '',
        aadhaar: invoice.aadhaar || '',
        gender: invoice.gender || 'Male',
        room_no: invoice.room_no,
        room_type: invoice.room_type || 'Single',
        security_deposit: Number(invoice.security_deposit) || 0,
        monthly_rent: Number(invoice.monthly_rent) || 0,
        maintenance_fees: Number(invoice.maintenance_fees) || 0,
        payment_date: invoice.payment_date,
        payment_month: invoice.payment_month,
        transaction_id: invoice.transaction_id || '',
        status: invoice.status || 'Paid',
        mail_sent: Boolean(invoice.mail_sent),
        mobile: invoice.mobile || '',
        photo: invoice.photo || null,
        qr_code: invoice.qr_code || null,
        receipt: invoice.receipt || null,
        notes: invoice.notes || null
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, invoice) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('invoices')
      .update({
        receipt_no: invoice.receipt_no,
        name: invoice.name,
        email: invoice.email,
        aadhaar: invoice.aadhaar,
        gender: invoice.gender,
        room_no: invoice.room_no,
        room_type: invoice.room_type,
        security_deposit: Number(invoice.security_deposit) || 0,
        monthly_rent: Number(invoice.monthly_rent) || 0,
        maintenance_fees: Number(invoice.maintenance_fees) || 0,
        payment_date: invoice.payment_date,
        payment_month: invoice.payment_month,
        transaction_id: invoice.transaction_id,
        status: invoice.status,
        mail_sent: Boolean(invoice.mail_sent),
        mobile: invoice.mobile,
        photo: invoice.photo,
        qr_code: invoice.qr_code,
        receipt: invoice.receipt,
        notes: invoice.notes
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const inventoryService = {
  async getAll() {
    return fetchAllRows('inventory', 'created_at', false);
  },

  async create(item) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('inventory')
      .insert([{
        item_name: item.item_name,
        category: item.category,
        quantity: Number(item.quantity) || 0,
        cost: Number(item.cost) || 0,
        condition: item.condition || 'Good',
        last_updated: item.last_updated || new Date().toISOString().split('T')[0]
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, item) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('inventory')
      .update({
        item_name: item.item_name,
        category: item.category,
        quantity: Number(item.quantity) || 0,
        cost: Number(item.cost) || 0,
        condition: item.condition || 'Good',
        last_updated: item.last_updated || new Date().toISOString().split('T')[0]
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('inventory')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const setupExpensesService = {
  async getAll() {
    return fetchAllRows('setup_expenses', 'payment_date', false);
  },

  async create(expense) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('setup_expenses')
      .insert([{
        item_name: expense.item_name,
        amount: Number(expense.amount) || 0,
        category: expense.category,
        payment_mode: expense.payment_mode || 'UPI',
        payment_date: expense.payment_date,
        status: expense.status || 'Paid',
        notes: expense.notes || ''
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, expense) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('setup_expenses')
      .update({
        item_name: expense.item_name,
        amount: Number(expense.amount) || 0,
        category: expense.category,
        payment_mode: expense.payment_mode || 'UPI',
        payment_date: expense.payment_date,
        status: expense.status || 'Paid',
        notes: expense.notes || ''
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('setup_expenses')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const inwardMoneyService = {
  async getAll() {
    return fetchAllRows('inward_money', 'date', false);
  },

  async create(record) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('inward_money')
      .insert([{
        source: record.source,
        amount: Number(record.amount) || 0,
        date: record.date,
        description: record.description || ''
      }])
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async update(id, record) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { data, error } = await supabase
      .from('inward_money')
      .update({
        source: record.source,
        amount: Number(record.amount) || 0,
        date: record.date,
        description: record.description || ''
      })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data?.[0];
  },

  async delete(id) {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const { error } = await supabase
      .from('inward_money')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  }
};

export const authService = {
  async signIn(email, password) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  },

  async signUp(email, password, metadata = {}) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });

    if (error) throw error;
    return data;
  },

  async signOut() {
    if (!isSupabaseConfigured()) {
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getSession() {
    if (!isSupabaseConfigured()) {
      return null;
    }

    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data?.session || null;
  },

  async getUser() {
    if (!isSupabaseConfigured()) {
      return null;
    }

    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data?.user || null;
  },

  async resetPasswordForEmail(email) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured.');
    }

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + (process.env.PUBLIC_URL || '') + '/reset-password',
    });

    if (error) throw error;
    return data;
  },

  onAuthStateChange(callback) {
    if (!isSupabaseConfigured()) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    return supabase.auth.onAuthStateChange(callback);
  }
};


